import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import AccordionPanel from '../AccordionPanel/AccordionPanel.js';
import { IAccordionPanelProps } from '../AccordionPanel/models/IAccordionPanelProps.js';

import { IAccordionConfiguration, IAccordionProps, IAccordionState } from './models/index.js';
import Wrapper from './styledComponents/Wrapper.js';
import { ACCORDION_CATEGORY_KEY, ACCORDION_COMPONENT_KEY } from './Accordion.meta.js';

export class Accordion extends ComponentBase<IAccordionProps, IAccordionState, IAccordionConfiguration> {
	categoryKey = ACCORDION_CATEGORY_KEY;
	componentKey = ACCORDION_COMPONENT_KEY;

	panels: IAccordionPanelProps[] = [];

	constructor(props: IAccordionProps) {
		super(props);

		makeObservable(this, {
			panels: observable,
			initializePanelProps: action,
			panelsFromChildren: computed,
			togglePanel: action,
			accordionPanels: computed,
		});

		this.initializePanelProps();
	}

	initializePanelProps() {
		const { panels = [] } = this.props;
		this.panels = panels.length > 0 ? panels : this.panelsFromChildren;
	}

	get panelsFromChildren(): IAccordionPanelProps[] {
		const { children } = this.props;
		return React.Children.map(children, (child: any) => {
			if (React.isValidElement(child) && child.type === AccordionPanel) {
				return {
					...((child.props || {}) as any),
				} as IAccordionPanelProps;
			}
		}) as IAccordionPanelProps[];
	}

	togglePanel = (key: string, isOpen: boolean) => {
		const { showMultiple = false } = this.props;
		if (isOpen && !showMultiple) {
			this.panels = this.panels.map((panel) => {
				return {
					...panel,
					isOpen: panel.panelKey === key,
				};
			});
		}
	};

	get accordionPanels() {
		return this.panels.map((panel: any, index: number) => {
			const key = (panel && panel.panelKey) || `panel_${index}`;
			return <AccordionPanel {...panel} key={key} onTogglePanel={this.togglePanel} />;
		});
	}

	render() {
		const { className } = this.props;
		return <Wrapper className={className}>{this.accordionPanels}</Wrapper>;
	}
}

export default InjectedStyledComponent(observer(Accordion), ACCORDION_CATEGORY_KEY, ACCORDION_COMPONENT_KEY);
